import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export const TILE_URL = process.env.REACT_APP_MAP_TILE_URL || "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
export const TILE_ATTRIBUTION = process.env.REACT_APP_MAP_TILE_ATTRIBUTION ||
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
export const RED_DEER = [52.2681, -113.8112];

export const MAP_COLORS = {
  cleaned: "#2f7a4a",
  still_clean: "#6f8fa3",
  needs_cleaning: "#b23a2e",
  route: "#1e3a4c",
  route_stop: "#e8a317",
  live: "#1f6feb",
};

// A small triangle for each place the unit stood still on its route.
const ROUTE_STOP_ICON = L.divIcon({
  className: "route-stop-icon",
  html: `<svg viewBox="0 0 16 14" width="16" height="14" aria-hidden="true"><path d="M8 1 L15 13 H1 Z" fill="${MAP_COLORS.route_stop}" stroke="${MAP_COLORS.route}" stroke-width="1.5" stroke-linejoin="round"/></svg>`,
  iconSize: [16, 14],
  iconAnchor: [8, 9],
  tooltipAnchor: [0, -8],
});

// A blue van in a white ring for each unit's live position.
export const VAN_SVG = `<svg viewBox="0 0 32 32" width="32" height="32" aria-hidden="true">
<circle cx="16" cy="16" r="15" fill="${MAP_COLORS.live}" stroke="#ffffff" stroke-width="2"/>
<path d="M6.5 11.5 h12.5 l4.5 4.5 h1.5 v4.5 h-18.5 z" fill="#ffffff"/>
<path d="M19.8 12.8 h-3.3 v3.2 h6.5 z" fill="${MAP_COLORS.live}"/>
<rect x="8.3" y="12.8" width="6.6" height="3.2" rx="0.6" fill="${MAP_COLORS.live}"/>
<circle cx="10.5" cy="20.6" r="2.2" fill="#1b2328" stroke="#ffffff" stroke-width="1.2"/>
<circle cx="21" cy="20.6" r="2.2" fill="#1b2328" stroke="#ffffff" stroke-width="1.2"/>
</svg>`;
const VAN_ICON = L.divIcon({ className: "live-van-icon", html: VAN_SVG, iconSize: [32, 32], iconAnchor: [16, 16], tooltipAnchor: [0, -16] });

function minutesAgo(value, nowMs) {
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return "";
  const mins = Math.max(0, Math.round((nowMs - t) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  return `${hours} h ${mins % 60} min ago`;
}

const STATUS_TEXT = { cleaned: "Cleaned", still_clean: "Still clean", needs_cleaning: "Needs cleaning" };
const DRAW_ORDER = { still_clean: 0, cleaned: 1, needs_cleaning: 2 };
const MARKER_STYLE = {
  cleaned: { radius: 7, weight: 2, border: "#ffffff", fillOpacity: 0.95 },
  needs_cleaning: { radius: 6, weight: 2, border: "#ffffff", fillOpacity: 0.95 },
  still_clean: { radius: 4, weight: 1, border: null, fillOpacity: 0.7 },
};

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

// colorBy "tag" fills each stop with its tag's colour (tagColors) and keeps
// the status in the marker size; "status" (the default) colours by status.
// `track.stops` are drawn as triangles while `showRouteStops` is on; hovering
// one shows how long the unit stood there. `live` units get a blue van icon.
export default function StopMap({ rows, track, showRouteStops, live, formatClock, formatDuration, describeUpkeep, onShowVisits, colorBy, tagColors, tagLabels }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const stopsLayerRef = useRef(null);
  const trackLayerRef = useRef(null);
  const routeStopsLayerRef = useRef(null);
  const liveLayerRef = useRef(null);
  const rendererRef = useRef(null);
  const fittedRef = useRef(false);
  const showVisitsRef = useRef(onShowVisits);

  useEffect(() => {
    showVisitsRef.current = onShowVisits;
  }, [onShowVisits]);

  useEffect(() => {
    const map = L.map(containerRef.current, { preferCanvas: true, zoomControl: true }).setView(RED_DEER, 12);
    L.tileLayer(TILE_URL, { maxZoom: 19, attribution: TILE_ATTRIBUTION }).addTo(map);
    rendererRef.current = L.canvas({ padding: 0.5 });
    stopsLayerRef.current = L.layerGroup().addTo(map);
    trackLayerRef.current = L.layerGroup().addTo(map);
    routeStopsLayerRef.current = L.layerGroup().addTo(map);
    liveLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = stopsLayerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const located = rows
      .filter((r) => r.lat != null && r.lng != null)
      .sort((a, b) => (DRAW_ORDER[a.status] || 0) - (DRAW_ORDER[b.status] || 0));

    const byTag = colorBy === "tag";
    located.forEach((r) => {
      const statusColor = MAP_COLORS[r.status] || MAP_COLORS.needs_cleaning;
      const color = byTag ? ((tagColors && tagColors[r.classification]) || statusColor) : statusColor;
      const style = MARKER_STYLE[r.status] || MARKER_STYLE.needs_cleaning;
      const marker = L.circleMarker([r.lat, r.lng], {
        renderer: rendererRef.current,
        radius: style.radius,
        color: byTag ? "#ffffff" : style.border || color,
        weight: byTag ? Math.max(style.weight, 1) : style.weight,
        fillColor: color,
        fillOpacity: style.fillOpacity,
      });
      const popup = el("div", "map-popup");
      popup.appendChild(el("strong", null, r.name));
      if (tagLabels && tagLabels[r.classification]) popup.appendChild(el("div", null, tagLabels[r.classification]));
      popup.appendChild(el("div", null, STATUS_TEXT[r.status] || r.status));
      const upkeepText = describeUpkeep ? describeUpkeep(r) : "";
      if (upkeepText) popup.appendChild(el("div", null, upkeepText));
      if (r.cleaned_via) popup.appendChild(el("div", "map-popup-manual", `Cleaned together with ${r.cleaned_via.name}`));
      if (r.manual) {
        popup.appendChild(el("div", "map-popup-manual", `Marked cleaned by hand${r.manual.note ? `: ${r.manual.note}` : ""}`));
      }
      if (r.first_arrival) {
        popup.appendChild(el("div", null, `Arrived ${formatClock(r.first_arrival)}, stayed up to ${formatDuration(r.longest_s)}`));
      }
      if (r.units && r.units.length) popup.appendChild(el("div", null, r.units.join(", ")));
      const button = el("button", "link-btn map-popup-btn", "Show visits");
      button.type = "button";
      button.addEventListener("click", () => {
        map.closePopup();
        if (showVisitsRef.current) showVisitsRef.current(r.stop_id);
      });
      popup.appendChild(button);
      marker.bindPopup(popup);
      marker.addTo(layer);
    });

    if (!fittedRef.current && located.length) {
      map.fitBounds(L.latLngBounds(located.map((r) => [r.lat, r.lng])), { padding: [24, 24], maxZoom: 15 });
      fittedRef.current = true;
    }
  }, [rows, formatClock, formatDuration, describeUpkeep, colorBy, tagColors, tagLabels]);

  useEffect(() => {
    const map = mapRef.current;
    const layer = trackLayerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    if (!track || !track.points || !track.points.length) return;

    const line = track.points.map((p) => [p.lat, p.lng]);
    L.polyline(line, { renderer: rendererRef.current, color: MAP_COLORS.route, weight: 3, opacity: 0.8 }).addTo(layer);

    const start = track.points[0];
    const end = track.points[track.points.length - 1];
    L.circleMarker([start.lat, start.lng], { renderer: rendererRef.current, radius: 6, color: MAP_COLORS.route, weight: 3, fillColor: "#ffffff", fillOpacity: 1 })
      .bindTooltip(`Start ${formatClock(start.t)}`)
      .addTo(layer);
    L.circleMarker([end.lat, end.lng], { renderer: rendererRef.current, radius: 6, color: "#ffffff", weight: 2, fillColor: MAP_COLORS.route, fillOpacity: 1 })
      .bindTooltip(`End ${formatClock(end.t)}`)
      .addTo(layer);

    map.fitBounds(L.latLngBounds(line), { padding: [24, 24], maxZoom: 16 });
  }, [track, formatClock]);

  useEffect(() => {
    const layer = routeStopsLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!showRouteStops || !track || !track.stops) return;
    track.stops.forEach((s) => {
      const tip = el("div", "map-popup");
      const upTo = s.max_s != null && Math.round(s.max_s) > Math.round(s.duration_s) ? ` (up to ${formatDuration(s.max_s)})` : "";
      tip.appendChild(el("strong", null, `Stopped ${formatDuration(s.duration_s)}${upTo}`));
      tip.appendChild(el("div", null, `${formatClock(s.start)} to ${formatClock(s.end)}`));
      if (s.landmarks && s.landmarks.length) {
        const result = s.visit_status === "cleaned" ? "cleaned" : s.visit_status === "short" ? "too short" : "";
        tip.appendChild(el("div", null, `At ${s.landmarks.join(", ")}${result ? ` (${result})` : ""}`));
      } else {
        tip.appendChild(el("div", "muted", "Not at a landmark"));
      }
      L.marker([s.lat, s.lng], { icon: ROUTE_STOP_ICON, keyboard: false, zIndexOffset: 200 })
        .bindTooltip(tip, { direction: "top" })
        .addTo(layer);
    });
  }, [track, showRouteStops, formatClock, formatDuration]);

  useEffect(() => {
    const layer = liveLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!live || !live.length) return;
    const nowMs = Date.now();
    live.forEach((u) => {
      if (u.lat == null || u.lng == null) return;
      const tip = el("div", "map-popup");
      tip.appendChild(el("strong", null, u.device_name));
      const moving = u.speed_kph != null && u.speed_kph > 3;
      tip.appendChild(el("div", null, moving ? `Moving, ${Math.round(u.speed_kph)} km/h` : "Stopped"));
      tip.appendChild(el("div", "muted", `Position from ${minutesAgo(u.t, nowMs)} (${formatClock(u.t)})`));
      L.marker([u.lat, u.lng], { icon: VAN_ICON, keyboard: false, zIndexOffset: 1000 })
        .bindTooltip(tip, { direction: "top" })
        .addTo(layer);
    });
  }, [live, formatClock]);

  return <div ref={containerRef} className="map" role="region" aria-label="Map of stops" />;
}
